import type { ColumnDef } from "@tanstack/react-table";
import { IoArrowUpCircle } from "react-icons/io5";

import { depositIcon } from "../../assets/images";
import type { BaseRowItem } from "../../components/data-table";

export const PAYROLL_TABLE_LIMIT = 25;

function ordinalSuffix(day: number): string {
  switch (day) {
    case 1:
    case 21:
    case 31:
      return "st";
    case 2:
    case 22:
      return "nd";
    case 3:
    case 23:
      return "rd";
    default:
      return "th";
  }
}

function formatPayrollDate(value: unknown): string {
  if (!value) return "—";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.getDate();
  return `${date.toLocaleString("default", {
    month: "short",
  })} ${day}${ordinalSuffix(day)}, ${date.getFullYear()}, ${date.toLocaleString(
    "default",
    { hour: "numeric", minute: "numeric", hour12: true }
  )}`;
}

function amountText(value: unknown): string {
  const n = Number(value);
  if (Number.isNaN(n)) return "₦ 0.00";
  return `₦ ${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export type PayrollActivityRow = BaseRowItem & {
  raw: any;
  amount: string;
  email: string;
  transId: string;
  date: string;
};

export function buildPayrollActivityRows(records: any[]): PayrollActivityRow[] {
  return records.map((record, index) => ({
    id: record?._id ?? `payroll-${index}`,
    raw: record,
    amount: amountText(record?.amount),
    email: record?.staff?.email ?? "----",
    transId: record?._id ?? "—",
    date: formatPayrollDate(record?.createdAt || record?.processedAt),
  }));
}

export function createPayrollActivityColumns(): ColumnDef<
  PayrollActivityRow,
  unknown
>[] {
  return [
    {
      accessorKey: "type",
      header: "Type",
      cell: () => (
        <div className="min-w-0">
          <span className="flex items-center gap-1 text-[13px] font-medium text-white">
            <IoArrowUpCircle className="text-[16px]" />
            Debit
          </span>
          <span className="mt-0.5 block text-[11px] text-[#869AA9]">Hizo</span>
        </div>
      ),
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ getValue }) => (
        <div className="min-w-0">
          <span className="block text-[13px] text-white">
            {String(getValue())}
          </span>
          <span className="mt-0.5 flex items-center gap-1 text-[11px] text-[#869AA9]">
            <img
              src={depositIcon}
              alt=""
              className="h-[12px] w-[12px] object-contain"
            />
            Payroll
          </span>
        </div>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      meta: { hideBelow: "sm" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#AEBCC6]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "transId",
      header: "Trans ID",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="font-mono text-[11px] text-[#869AA9]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: () => (
        <span className="inline-flex rounded-full bg-[#013019] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#03C668]">
          COMPLETED
        </span>
      ),
    },
    {
      accessorKey: "date",
      header: "Date",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-[#869AA9]">{String(getValue())}</span>
      ),
    },
  ];
}

function csvCell(value: unknown): string {
  const s = value == null ? "" : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildPayrollActivitiesCsv(records: any[]): string {
  const header = ["Email", "Amount (NGN)", "Transaction ID", "Status", "Date"];
  const rows = records.map((r) => [
    csvCell(r?.staff?.email ?? ""),
    csvCell(amountText(r?.amount)),
    csvCell(r?._id ?? ""),
    csvCell("COMPLETED"),
    csvCell(formatPayrollDate(r?.createdAt || r?.processedAt)),
  ]);
  return [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
}
